import { StudentAcademicStatusDTO } from "./studentAcademicStatus.dto"
import {
  calendarDaysSince,
  formatScoreAgeLong,
  isScoreFreshForFlag,
} from "./scoreRecency"

// One source for "this row needs attention" — the pill counts rows with a
// non-empty signal list, the Attention column renders the signals as chips,
// and the signal predicates cut by signal key. Spec §5 thresholds:
export const PROGRESS_STALL_AFTER_DAYS = 14
export const NO_SESSION_AFTER_DAYS = 7
// Score ceilings for flagging (fresh scores only) — exported so the filter
// predicates can label themselves with the same numbers this logic uses.
export const CSAT_FLAG_MAX = 2
export const NPS_FLAG_MAX = 6

export type AttentionSignalKey = "csat" | "nps" | "stall" | "noSession"

export type AttentionSignal = {
  key: AttentionSignalKey
  label: string
}

// Plain-language definitions, surfaced wherever a signal shows (Mayer read
// "stalled" and had to ask what it meant) — quoting the same constants the
// flagging logic uses, and naming each score's scale.
export const SIGNAL_DEFINITIONS: Record<AttentionSignalKey, string> = {
  csat: `Rated their satisfaction ${CSAT_FLAG_MAX} or lower (scale 1–5) within the last 30 days`,
  nps: `Answered "would you recommend us" with ${NPS_FLAG_MAX} or lower (scale 0–10) within the last 30 days`,
  stall: `No learning progress for more than ${PROGRESS_STALL_AFTER_DAYS} days`,
  noSession: `No live-session attendance for more than ${NO_SESSION_AFTER_DAYS} days`,
}

// Every signal is honest about recency and data existence:
// - CSAT <= 2 / NPS <= 6 only while fresh (submitted within 30 days) — a
//   January 2 and a fresh 2 must never flag alike. Cell colour keeps its
//   wider thresholds (CSAT < 4 / NPS < 8 on non-stale scores), so a fresh 3
//   can show a red cell without flagging the row.
// - Progress stalled / no live session fire only while the timestamp exists;
//   a student with no data is unknown, not flagged.
// - "Program running" (spec §5) is satisfied by tab scoping: the Attention
//   column and the pill exist on the Active tab only.
export function getAttentionSignals(
  student: StudentAcademicStatusDTO,
): AttentionSignal[] {
  const signals: AttentionSignal[] = []
  const csat = student.status.satisfaction.csat
  if (
    csat &&
    csat.score <= CSAT_FLAG_MAX &&
    isScoreFreshForFlag(csat.submittedAt)
  ) {
    signals.push({
      key: "csat",
      label: `CSAT ${csat.score} · rated ${formatScoreAgeLong(
        csat.submittedAt,
      )}`,
    })
  }
  const nps = student.status.satisfaction.nps
  if (
    nps &&
    nps.score <= NPS_FLAG_MAX &&
    isScoreFreshForFlag(nps.submittedAt)
  ) {
    signals.push({
      key: "nps",
      label: `NPS ${nps.score} · rated ${formatScoreAgeLong(nps.submittedAt)}`,
    })
  }
  const lastActivity = student.status.activity.lastLearningActivityTimestamp
  if (lastActivity) {
    const days = calendarDaysSince(lastActivity)
    if (days !== null && days > PROGRESS_STALL_AFTER_DAYS) {
      // "no progress", not "stalled": the jargon word needed explaining.
      signals.push({ key: "stall", label: `no progress ${days}d` })
    }
  }
  const lastSession = student.status.activity.lastLiveSessionAttendanceTimestamp
  if (lastSession) {
    const days = calendarDaysSince(lastSession)
    if (days !== null && days > NO_SESSION_AFTER_DAYS) {
      signals.push({ key: "noSession", label: `no session ${days}d` })
    }
  }
  return signals
}

export function isStudentFlagged(student: StudentAcademicStatusDTO): boolean {
  return getAttentionSignals(student).length > 0
}
